import { createSlice } from "@reduxjs/toolkit";
import {
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  MarkerType,
} from "@xyflow/react";

export const flow = createSlice({
  name: "flow",
  initialState: {
    nodes: JSON.parse(localStorage.getItem("board"))?.state?.node || [],
    edges: JSON.parse(localStorage.getItem("board"))?.state?.edge || [],
    viewport: { x: 0, y: 0, zoom: 1 },
  },

  reducers: {
    setState: (state, action) => {
      state.nodes = action.payload.node;
      state.edges = action.payload.edge;
    },
    updateViewport: (state, action) => {
      state.viewport = action.payload.viewport;
    },
    addNode: (state, action) => {
      // To generate a unique id for the new node, we will use the following logic:
      let id;
      const existingIds = state.nodes.map((node) => node.id);
      for (let i = 1; ; i++) {
        id = `Node ${i}`;
        if (!existingIds.includes(id)) {
          break;
        }
      }
      let newNode;
      if (action.payload.type === "imageNode") {
        newNode = {
          id: id,
          type: action.payload.type,
          data: {
            id: id,
            imageUrl: action.payload.imageUrl || null,
            script: action.payload.script || null,
          },
          position: {
            x: -state.viewport.x + 350 + (Math.random() - 0.5) * 50,
            y: -state.viewport.y + 250 + (Math.random() - 0.5) * 50,
          },
        };
      } else if (
        action.payload.type === "instagramNode" ||
        action.payload.type === "youtubeNode" ||
        action.payload.type === "tiktokNode" ||
        action.payload.type === "facebookNode"
      ) {
        newNode = {
          id: id,
          type: action.payload.type,
          data: {
            id: id,
            imageUrl: action.payload.imageUrl || null,
            videoUrl: action.payload.videoUrl || null,
            sourceUrl: action.payload.sourceUrl || null,
            script: action.payload.script || null,
            title: action.payload.title || null,
          },
          position: {
            x: -state.viewport.x + 350 + (Math.random() - 0.5) * 50,
            y: -state.viewport.y + 250 + (Math.random() - 0.5) * 50,
          },
        };
      } else if (action.payload.type === "documentNode") {
        newNode = {
          id: id,
          type: action.payload.type,
          data: {
            id: id,
            file: action.payload.file || null,
            script: action.payload.script || null,
            title: action.payload.title || null,
          },
          position: {
            x: -state.viewport.x + 350 + (Math.random() - 0.5) * 50,
            y: -state.viewport.y + 250 + (Math.random() - 0.5) * 50,
          },
        };
      } else if (action.payload.type === "voiceRecordNode") {
        newNode = {
          id: id,
          type: action.payload.type,
          data: {
            id: id,
            audioUrl: action.payload.audioUrl || null,
            script: action.payload.script || null,
            title: action.payload.title || null,
          },
          position: {
            x: -state.viewport.x + 350 + (Math.random() - 0.5) * 50,
            y: -state.viewport.y + 250 + (Math.random() - 0.5) * 50,
          },
        };
      } else if (action.payload.type === "aichatNode") {
        newNode = {
          id: id,
          type: action.payload.type,
          data: {
            id: id,
            AIModel: null,
          },
          position: {
            x: -state.viewport.x + 350 + (Math.random() - 0.5) * 50,
            y: -state.viewport.y + 250 + (Math.random() - 0.5) * 50,
          },
        };
      } else {
        newNode = {
          id: id,
          type: action.payload.type,
          data: {
            id: id,
            script: action.payload.script || null,
          },
          position: {
            x: -state.viewport.x + 350 + (Math.random() - 0.5) * 50,
            y: -state.viewport.y + 250 + (Math.random() - 0.5) * 50,
          },
        };
      }

      state.nodes.push(newNode);
    },

    updateNode: (state, action) => {
      const { id, data } = action.payload;
      if (data.grouped) {
        const updatingNode = state.nodes.find((node) => node.id === id);
        const parentNode = state.nodes.find(
          (node) => node.id === data.parentNodeId
        );
        const updatingNodeIdx = state.nodes.indexOf(updatingNode);
        const parentNodeIdx = state.nodes.indexOf(parentNode);
        if (updatingNodeIdx > parentNodeIdx) {
          state.nodes[updatingNodeIdx] = {
            ...updatingNode,
            parentId: data.parentNodeId,
            position: data.position,
            extent: "parent",
          };
        } else {
          state.nodes[parentNodeIdx] = {
            ...updatingNode,
            parentId: data.parentNodeId,
            position: data.position,
            extent: "parent",
          };
          state.nodes[updatingNodeIdx] = parentNode;
        }
      } else {
        state.nodes = state.nodes.map((node) => {
          if (node.id === id) {
            node.data = data;
          }
          return node;
        });
      }
    },

    ungroupNode: (state, action) => {
      const childNode = state.nodes.find(
        (node) => node.id === action.payload.id
      );
      const parentNode = state.nodes.find(
        (node) => node.id === childNode.parentId
      );
      state.nodes = state.nodes.map((node) => {
        if (node.id === action.payload.id) {
          return {
            id: node.id,
            type: node.type,
            data: node.data,
            position: {
              x: node.position.x + parentNode.position.x,
              y: node.position.y + parentNode.position.y,
            },
          };
        }
        return node;
      });
    },

    deleteNode: (state, action) => {
      const { id, type } = action.payload;
      if (action.payload.type === type) {
        const parentNode = state.nodes.find((node) => node.id === id);
        state.nodes = state.nodes.map((node) => {
          if (node.parentId === id) {
            return {
              id: node.id,
              type: node.type,
              data: node.data,
              position: {
                x: node.position.x + parentNode.position.x,
                y: node.position.y + parentNode.position.y,
              },
            };
          }
          return node;
        });
      }
      state.nodes = state.nodes.filter((node) => node.id !== id);
      state.edges = state.edges.filter(
        (edge) => edge.target !== id && edge.source !== id
      );
    },

    onNodesChange: (state, action) => {
      state.nodes = applyNodeChanges(action.payload, state.nodes).map(
        (node) => {
          const change = action.payload.find((c) => c.id === node.id);
          if (change?.resized) {
            // Update size for resized nodes
            return {
              ...node,
              width: change.width || node.width,
              height: change.height || node.height,
            };
          }
          return node;
        }
      );
    },
    onEdgesChange: (state, action) => {
      state.edges = applyEdgeChanges(action.payload, state.edges);
    },
    onConnect: (state, action) => {
      state.edges = addEdge(
        {
          ...action.payload,
          type: "customEdge",
          animated: true,
          zIndex: 10,
          markerEnd: {
            type: MarkerType.ArrowClosed,
          },
        },
        state.edges
      );
    },
    onEdgeClick: (state, action) => {
      state.edges = state.edges.filter((edge) => edge.id !== action.payload.id);
    },
  },
});

export const {
  setState,
  addNode,
  updateNode,
  ungroupNode,
  deleteNode,
  onNodesChange,
  onEdgesChange,
  onConnect,
  updateViewport,
  onEdgeClick,
} = flow.actions;

export default flow.reducer;
